import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { debounce } from 'lodash'

import { Badge } from '~/components/UI/Badge'
import { formatInputMoneyToNumber, formatMoney } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

const FIELD_NAME = 'total_budget'

export function useEditableTotalBudget({
  formRef,
  isLoading,
  values,
  hasUploadedMedia,
  reproductions,
  setRequiredData,
  setTotalBudgetQuery,
  hasToFillFields,
  isForm
}: BudgetComponentProps) {
  const { t } = useAPPTranslation()
  const { lengthCentsCurrency } = useAppInfo()

  const [minTotalBudgetLabel, setMinTotalBudgetLabel] = useState(null)
  const [maxTotalBudgetLabel, setMaxTotalBudgetLabel] = useState(null)
  const [totalBudget, setTotalBudget] = useState(null)

  const isInvestmentValid = useCallback(
    (totalBudgetValue: number) =>
      !!reproductions.canPurchaseReproductions &&
      !!totalBudgetValue &&
      !!values.totalCostValue &&
      totalBudgetValue >= values.totalCostValue,
    [values.totalCostValue, reproductions.canPurchaseReproductions]
  )

  const totalBudgetValue = useMemo(() => {
    const totalBudgetValue = formatInputMoneyToNumber(
      totalBudget,
      lengthCentsCurrency
    )

    if (!isForm) return totalBudget

    setRequiredData((prev: RequiredDataToSubmit) => ({
      ...prev,
      isInvestmentValid: isInvestmentValid(totalBudgetValue)
    }))

    return totalBudgetValue
  }, [totalBudget, lengthCentsCurrency, isInvestmentValid])

  const isReproductionsValid = useMemo(
    () =>
      !!reproductions.purchaseReproductions &&
      !!reproductions.canPurchaseReproductions,
    [
      reproductions.purchaseReproductions,
      reproductions.canPurchaseReproductions
    ]
  )

  const isTotalBudgetLowerThanMin = useMemo(
    () =>
      !!reproductions.canPurchaseReproductions &&
      !!totalBudgetValue &&
      !!values.totalCostValue &&
      totalBudgetValue < values.totalCostValue,
    [
      reproductions.canPurchaseReproductions,
      totalBudgetValue,
      values.totalCostValue
    ]
  )

  const isTotalBudgetGreaterThanMax = useMemo(
    () =>
      !!totalBudgetValue &&
      !!values.totalCostValue &&
      totalBudgetValue >= values.totalCostValue &&
      !isReproductionsValid,
    [totalBudgetValue, values.totalCostValue, isReproductionsValid]
  )

  const handleDebounce = useRef(
    debounce((func: () => void) => func(), 500)
  ).current

  function handleChange(e: InputMoneyValuesOnChange) {
    setTotalBudget(e)
    handleDebounce(() =>
      setTotalBudgetQuery(formatInputMoneyToNumber(e, lengthCentsCurrency))
    )
  }

  const hasNoInventory =
    !hasToFillFields && reproductions.totalReproductions === 0
  const isInputDisabled = hasToFillFields || hasNoInventory

  const showBadgeIsInvalid = useMemo(
    () => !isInvestmentValid(totalBudgetValue) && !isLoading,
    [isInvestmentValid, totalBudgetValue, isLoading]
  )

  const showBadgeIsValid = useMemo(
    () => isInvestmentValid(totalBudgetValue) && !isLoading,
    [isInvestmentValid, totalBudgetValue, isLoading]
  )

  const sectionIsValid = useMemo(
    () => isInvestmentValid(totalBudgetValue),
    [totalBudgetValue, isInvestmentValid]
  )

  const BudgetContent = useMemo(() => {
    if (isTotalBudgetLowerThanMin) {
      return t('rm:unavailable')
    }

    if (isTotalBudgetGreaterThanMax) {
      return t(
        'rm:campaign.create.Investment.editableTotalBudget.unavailableInventory'
      )
    }

    if (isReproductionsValid) {
      return reproductions.purchaseReproductions
    }

    return <Badge template="warning">{t('rm:pending')}</Badge>
  }, [
    isTotalBudgetLowerThanMin,
    isTotalBudgetGreaterThanMax,
    isReproductionsValid,
    reproductions.purchaseReproductions,
    t
  ])

  const shouldShowMinMaxLabel = useMemo(
    () => !!minTotalBudgetLabel && !!maxTotalBudgetLabel,
    [minTotalBudgetLabel, maxTotalBudgetLabel]
  )

  useEffect(() => {
    if (values.totalCost) {
      setMinTotalBudgetLabel(values.totalCost)
    } else {
      setMinTotalBudgetLabel(null)
    }
  }, [values.totalCost])

  useEffect(() => {
    if (hasUploadedMedia && !!reproductions.maxTotalBudget) {
      setMaxTotalBudgetLabel(formatMoney(reproductions.maxTotalBudget))
    } else {
      setMaxTotalBudgetLabel(null)
    }
  }, [reproductions.maxTotalBudget, hasUploadedMedia])

  useEffect(() => {
    if (isTotalBudgetLowerThanMin) {
      formRef?.current?.setErrors({
        [FIELD_NAME]: t(
          'rm:campaign.create.Investment.editableTotalBudget.valueLower'
        )
      })
    } else if (isTotalBudgetGreaterThanMax) {
      formRef?.current?.setErrors({
        [FIELD_NAME]: t(
          'rm:campaign.create.Investment.editableTotalBudget.valueGreater',
          {
            maxTotalBudget: maxTotalBudgetLabel
          }
        )
      })
    } else {
      formRef?.current?.setErrors({
        [FIELD_NAME]: null
      })
    }
  }, [
    isTotalBudgetLowerThanMin,
    isTotalBudgetGreaterThanMax,
    maxTotalBudgetLabel,
    formRef,
    t
  ])

  return {
    handleChange,
    hasNoInventory,
    isInputDisabled,
    showBadgeIsInvalid,
    showBadgeIsValid,
    sectionIsValid,
    minTotalBudgetLabel,
    maxTotalBudgetLabel,
    shouldShowMinMaxLabel,
    totalBudget,
    BudgetContent,
    FIELD_NAME
  }
}
